import React from 'react';
import { Link } from 'react-router-dom';
import { Truck, MapPin, Phone, MessageCircle, Send, CheckCircle, ArrowRight, Clock, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import Layout from '../components/layout/Layout';
import { companyInfo } from '../data/mock';

// Данные городов с ссылками
const citiesWithLinks = {
  bashkortostan: [
    { name: 'Туймазы', slug: 'tuymazy' },
    { name: 'Октябрьский', slug: 'oktyabrsky' },
    { name: 'Белебей', slug: 'belebey' },
    { name: 'Приютово', slug: null },
    { name: 'Буздяк', slug: null },
    { name: 'Ермекеево', slug: null },
    { name: 'Чекмагуш', slug: null },
    { name: 'Шаран', slug: null },
    { name: 'Благоварский р-н', slug: null },
    { name: 'Бакалы', slug: null }
  ],
  tatarstan: [
    { name: 'Бавлы', slug: 'bavly' },
    { name: 'Азнакаево', slug: 'aznakaevo' },
    { name: 'Бугульма', slug: 'bugulma' },
    { name: 'Ютазы', slug: null },
    { name: 'Лениногорск', slug: null }
  ]
};

const DeliveryPage = () => {
  return (
    <Layout>
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-50 to-green-50 py-10">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-2">
            <Truck className="w-8 h-8 text-emerald-600" />
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Доставка теплиц</h1>
          </div>
          <p className="text-gray-600">Доставляем теплицы по Башкортостану и Татарстану</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Popular Cities Cards */}
        <div className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Популярные направления</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { slug: 'tuymazy', name: 'Туймазы', desc: 'Базовый город, самовывоз' },
              { slug: 'belebey', name: 'Белебей', desc: 'Доставка 2-3 раза в неделю' },
              { slug: 'oktyabrsky', name: 'Октябрьский', desc: 'Регулярные рейсы' },
              { slug: 'bavly', name: 'Бавлы', desc: 'Доставка в Татарстан' },
              { slug: 'aznakaevo', name: 'Азнакаево', desc: 'Усиленные каркасы' },
              { slug: 'bugulma', name: 'Бугульма', desc: 'Доставка по графику' }
            ].map(city => (
              <Link 
                key={city.slug}
                to={`/dostavka/${city.slug}`}
                className="bg-white rounded-xl p-5 border border-gray-200 hover:border-emerald-300 hover:shadow-md transition-all group"
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-900 group-hover:text-emerald-600">{city.name}</h3>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-emerald-600" />
                </div>
                <p className="text-sm text-gray-500">{city.desc}</p>
              </Link>
            ))}
          </div>
        </div>

        {/* Cities Lists */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Все города доставки</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Башкортостан */}
            <div className="bg-emerald-50 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <MapPin className="w-6 h-6 text-emerald-600" />
                <h3 className="text-xl font-semibold text-gray-900">Республика Башкортостан</h3>
              </div>
              <ul className="space-y-3">
                {citiesWithLinks.bashkortostan.map((city, index) => (
                  <li key={index} className="flex items-center">
                    <div className="flex items-center gap-2 text-gray-700">
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                      {city.slug ? (
                        <Link 
                          to={`/dostavka/${city.slug}`}
                          className="hover:text-emerald-600 hover:underline"
                        >
                          {city.name}
                        </Link>
                      ) : (
                        <span>{city.name}</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Татарстан */}
            <div className="bg-sky-50 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <MapPin className="w-6 h-6 text-sky-600" />
                <h3 className="text-xl font-semibold text-gray-900">Республика Татарстан</h3>
              </div>
              <ul className="space-y-3">
                {citiesWithLinks.tatarstan.map((city, index) => (
                  <li key={index} className="flex items-center">
                    <div className="flex items-center gap-2 text-gray-700">
                      <CheckCircle className="w-4 h-4 text-sky-500" />
                      {city.slug ? (
                        <Link 
                          to={`/dostavka/${city.slug}`}
                          className="hover:text-sky-600 hover:underline"
                        >
                          {city.name}
                        </Link>
                      ) : (
                        <span>{city.name}</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Delivery Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
              <Truck className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Доставка до участка</h3>
            <p className="text-sm text-gray-600">
              Привезём теплицу прямо к вам на участок в удобное время
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
              <Clock className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Сроки 1-3 дня</h3>
            <p className="text-sm text-gray-600">
              Доставка в течение 1-3 дней в зависимости от города
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
              <CreditCard className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Оплата при получении</h3>
            <p className="text-sm text-gray-600">
              Оплатите теплицу наличными или картой при получении
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-emerald-600 rounded-2xl p-8 text-center text-white">
          <h2 className="text-2xl font-bold mb-4">Закажите доставку теплицы</h2>
          <p className="text-emerald-100 mb-6 max-w-xl mx-auto">
            Позвоните нам или оставьте заявку — мы рассчитаем стоимость доставки в ваш город
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href={`tel:${companyInfo.phoneClean}`}>
              <Button size="lg" variant="secondary" className="bg-white text-emerald-600 hover:bg-gray-100">
                <Phone className="w-5 h-5 mr-2" />
                {companyInfo.phone}
              </Button>
            </a>
            <a href={`https://wa.me/${companyInfo.whatsapp}`} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-emerald-700 hover:bg-emerald-800">
                <MessageCircle className="w-5 h-5 mr-2" />
                WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DeliveryPage;
